import axios, { AxiosError } from "axios";
import type {
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { clearOnLogout, getToken, setItem } from "@/lib/utils";
import toast from "@/lib/toast";
import Cookies from "js-cookie";
import UserService from "@/services/api/user";

const config = {
  baseURL: window.location.href.includes('localhost') ? import.meta.env.VITE_API_BASE_URL : import.meta.env.VITE_API_TARGET_URL,
};

const publicRequest = axios.create(config);
const privateRequest = axios.create(config);
const referralRequest = axios.create({
  baseURL: import.meta.env.VITE_API_REFERRAL_URL,
});

// request interceptor (private)
const requestHandler = (
  request: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const url = request.url || "";

  if (url.includes("/LeadsInsight/") || url.includes("/Leaves")) {

    const mobileCookie = Cookies.get("mobile");

    if (mobileCookie) {
      try {
        const {
          MobileNo,
          UTCTimeStamp,
          Signature,
          UserId,
        } = JSON.parse(mobileCookie);

        request.headers = request.headers ?? {};

        request.headers.MobileNo = MobileNo;
        request.headers.UTCTimeStamp =
          UTCTimeStamp || new Date().toISOString();
        request.headers.Signature = Signature;
        request.headers.UserId = UserId;

      } catch (err) {
        console.error("Invalid mobile cookie", err);
      }
    } else {
      const token = getToken("access_token");

      if (token && localStorage.getItem("Permissions")) {
        request.headers = request.headers ?? {};
        request.headers.Authorization = token;
      }
    }

  } else {
    const basicToken = getToken("access_token");

    if (basicToken && localStorage.getItem("Permissions")) {
      request.headers = request.headers ?? {};
      request.headers.Authorization = basicToken;
    }
  }

  return request;
};

referralRequest.interceptors.request.use((config) => {
  let token = getToken("referraltoken");
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const responseErrorHandler = (error: AxiosError) => {
  // Intentionally aborted/canceled requests are not user-facing errors.
  if (axios.isCancel(error) || (error as any)?.code === "ERR_CANCELED") {
    return Promise.reject(error);
  }

  if (error.response) {
    const { status, data } = error.response;
    const message = (data as any)?.message || (data as any)?.Message || error.message;

    if (status === 401) {
      if (message?.toLowerCase().includes("token expired")) {
        clearOnLogout();
        window.location.href = "/";
        toast.warning("Token expired, please login");
      } else {
        toast.error(message || "Unauthorized request");
      }
    } else {
      toast.error(message || "Some Error Occurred");
    }
  } else {
    toast.error(error?.message || "Some Error Occurred");
  }

  return Promise.reject(error);
};

const PERMISSION_703_COUNT_KEY = "permission_703_count";

let isHandling703 = false;

const isOnDashboard = () => {
  const path = window.location.pathname;
  return path === "/dashboard" || path === "/";
};

const refreshPermissions = async () => {
  try {
    const raw = Cookies.get("currentUser");
    if (!raw) return;

    const userId = JSON.parse(raw)?.userId;
    if (!userId) return;

    const response = await UserService.getUserPermissions(userId);
    if ((response?.Status ?? response?.status) !== 200) return;

    setItem("Permissions", response?.data ?? []);
    window.location.reload();
  } catch {
    // silent: don't break existing flows
  }
};

const handle703 = () => {
  if (isHandling703) return;
  isHandling703 = true;

  const count = Number(sessionStorage.getItem(PERMISSION_703_COUNT_KEY) || 0);

  if (count === 0) {
    sessionStorage.setItem(PERMISSION_703_COUNT_KEY, "1");
    refreshPermissions();
    return;
  }

  if (count === 1) {
    if (isOnDashboard()) {
      sessionStorage.removeItem(PERMISSION_703_COUNT_KEY);
      window.location.href = "/unauthorized";
      return;
    }

    sessionStorage.setItem(PERMISSION_703_COUNT_KEY, "2");
    window.location.href = "/dashboard";
    return;
  }

  sessionStorage.removeItem(PERMISSION_703_COUNT_KEY);
  window.location.href = "/unauthorized";
};

privateRequest.interceptors.request.use(requestHandler, (error) =>
  Promise.reject(error)
);

privateRequest.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data;

    if (res?.Status === 401 || res?.status === 401) {
      clearOnLogout();
      toast.error(res?.Message || "Session expired. Please login again.");

      setTimeout(() => {
        const returnUrl =
          window.location.pathname +
          window.location.search +
          window.location.hash;

        window.location.href =
          `/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`;
      }, 1000);

      return Promise.reject(res);
    }

    // if (res?.Status === 703 || res?.status === 703) {
    //   handle703();
    //   return response;
    // }

    return response;
  },
  responseErrorHandler
);

interface ApiResponse<T = any> {
  Errors: string | null;
  Message: string;
  Status?: number;
  status?: number;
  data: T;
}

const handleResponse = (res: any, rejectWithValue?: any) => {
  const status = res?.Status || res?.status;

  const message =
    res?.Message ||
    res?.message ||
    "Something went wrong";

  // Success
  if ([200, 201, 404].includes(status)) {
    return res?.data;
  }

  if (rejectWithValue) {
    return rejectWithValue(message);
  }

  return null;
};

export { privateRequest, publicRequest, referralRequest, type ApiResponse, handleResponse };
